/**
 * 工具站顯示清單：從後端取得要顯示的工具 slug，過濾 TOOLS。
 * - 成功且回傳非空陣列 → 只顯示該批工具（導航與首頁皆受限制）。
 * - 未設定 VITE_API_BASE、或 API 失敗、或後端回傳空陣列 → 顯示全部工具。
 * - 若「有設定 API 但請求失敗」會設定 loadError，供 UI 顯示提示（例如 CORS / 網路問題）。
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { TOOLS, type ToolItem } from '../data/tools';
import { getApiBase } from '../lib/api';

interface ToolStationVisibleContextValue {
  /** 要顯示的工具（依後端設定過濾）；載入中或失敗時為全部 */
  tools: ToolItem[];
  isLoading: boolean;
  /** 有設定 API 但請求失敗（CORS/網路等）時為 true，此時仍顯示全部工具 */
  loadError: boolean;
}

const ToolStationVisibleContext = createContext<ToolStationVisibleContextValue>(
  {
    tools: TOOLS,
    isLoading: false,
    loadError: false,
  }
);

export function ToolStationVisibleProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [slugs, setSlugs] = useState<string[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    const apiBase = getApiBase();
    if (!apiBase) {
      setSlugs([]);
      setLoadError(false);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setLoadError(false);
    fetch(`${apiBase}/api/public/tool-station-visible`)
      .then(res => {
        if (cancelled) return { slugs: [] as string[] };
        if (!res.ok) {
          setLoadError(true);
          return { slugs: [] };
        }
        return res.json() as Promise<{ slugs?: string[] }>;
      })
      .then((data: { slugs?: string[] }) => {
        if (!cancelled && Array.isArray(data.slugs)) setSlugs(data.slugs);
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError(true);
          setSlugs([]);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const tools: ToolItem[] =
    slugs === null || slugs.length === 0
      ? TOOLS
      : TOOLS.filter(t => slugs.includes(t.slug));

  const value: ToolStationVisibleContextValue = {
    tools,
    isLoading,
    loadError,
  };

  return (
    <ToolStationVisibleContext.Provider value={value}>
      {children}
    </ToolStationVisibleContext.Provider>
  );
}

export function useToolStationVisible(): ToolStationVisibleContextValue {
  return useContext(ToolStationVisibleContext);
}
